import { call } from '@advanza/api'
import { formatPriceWithCurrency } from '@advanza/func'
import { showConfirmModal } from '@advanza/modal'
import { Button, Icon } from '@advanza/ui'
import InputField from '@trustoo/components/input/InputField'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import Restricted from 'components/Restricted'
import { useCallback, useEffect, useState } from 'react'

interface StatusData {
    threshold: number | null
    blockLimit: number | null
    percentage: number | null
    openBalance: number
    unpaidCreditLimitBrutoThisMonth: number
    totalBalance: number
    creditLimitCleared: number
    creditLimitReached: string | null
    isCustomLimits: boolean
}

interface LimitsData {
    effectiveLimits: number[]
    serviceLimits: number[]
    isCustom: boolean
}

interface Props {
    providerId: number
}

const STATUS_GREEN = '#4caf50'
const STATUS_ORANGE = '#ff9800'
const STATUS_RED = '#f44336'

function getStatusColor(percentage: number | null, creditLimitReached: string | null): string {
    if (creditLimitReached) {
        return STATUS_RED
    }

    if (percentage === null) {
        return STATUS_GREEN
    }

    if (percentage >= 100) {
        return STATUS_RED
    }

    if (percentage >= 75) {
        return STATUS_ORANGE
    }

    return STATUS_GREEN
}

function arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
        return false
    }

    return a.every((val, idx) => val === b[idx])
}

const ProviderCreditLimitSettings = ({ providerId }: Props) => {
    const [statusData, setStatusData] = useState<StatusData | undefined>()
    const [limitsData, setLimitsData] = useState<LimitsData | undefined>()
    const [isSaving, setIsSaving] = useState(false)
    const [newLimit, setNewLimit] = useState('')

    const fetchStatus = useCallback(
        () =>
            call(`office/providers/get-credit-limit-status/${providerId}`).then((response) =>
                setStatusData(response.data),
            ),
        [providerId],
    )

    const fetchLimits = useCallback(
        () =>
            call(`office/providers/get-provider-credit-limits/${providerId}`).then((response) =>
                setLimitsData(response.data),
            ),
        [providerId],
    )

    useEffect(() => {
        fetchStatus()
        fetchLimits()
    }, [fetchStatus, fetchLimits])

    const handleSaveLimits = (newLimits: number[]) => {
        setIsSaving(true)
        call(`office/providers/save-provider-credit-limits/${providerId}`, {
            method: 'POST',
            json: { limits: newLimits },
        })
            .then((response) => {
                setLimitsData(response.data)
                fetchStatus()
            })
            .finally(() => {
                setIsSaving(false)
                setNewLimit('')
            })
    }

    const handleAddLimit = () => {
        const limitValue = parseInt(newLimit, 10)

        if (isNaN(limitValue) || limitValue <= 0 || !limitsData) {
            return
        }

        if (limitsData.effectiveLimits.includes(limitValue)) {
            return
        }

        const newLimits = [...limitsData.effectiveLimits, limitValue].sort((a, b) => a - b)
        handleSaveLimits(newLimits)
    }

    const handleRemoveLimit = (limitToRemove: number) => {
        if (!limitsData) {
            return
        }

        const newLimits = limitsData.effectiveLimits.filter((limit) => limit !== limitToRemove)
        handleSaveLimits(newLimits)
    }

    const handleResetToStandard = () => {
        if (!limitsData) {
            return
        }

        handleSaveLimits(limitsData.serviceLimits)
    }

    const handleSetToNextThreshold = () => {
        setIsSaving(true)
        call(`office/providers/set-to-next-threshold/${providerId}`, { method: 'POST' })
            .then(() => {
                fetchStatus()
                fetchLimits()
            })
            .finally(() => setIsSaving(false))
    }

    const handleResetCreditLimitCleared = async () => {
        const confirmed = await showConfirmModal({
            header: 'Are you sure?',
            content: (
                <>
                    You are about to reset which credit limits are already cleared. Are you sure you
                    want to proceed?
                </>
            ),
            cancelContent: 'No, cancel',
            confirmContent: 'Yes, reset',
        })

        if (!confirmed) {
            return
        }

        setIsSaving(true)

        try {
            await call(`office/providers/reset-credit-limit-cleared/${providerId}`, {
                method: 'POST',
            })

            fetchStatus()
            fetchLimits()
        } finally {
            setIsSaving(false)
        }
    }

    if (!statusData || !limitsData) {
        return <LoadingDots />
    }

    const statusColor = getStatusColor(statusData.percentage, statusData.creditLimitReached)
    const isCustom = limitsData.isCustom
    const isDifferentFromBranch = !arraysEqual(limitsData.effectiveLimits, limitsData.serviceLimits)

    return (
        <div>
            <div
                className="inline-block mb-4 rounded-lg border p-4"
                style={{ borderColor: statusColor }}>
                <div className="inline-flex gap-6">
                    <div>
                        <div className="text-md text-grey-800">Open Balance</div>
                        <div className="text-xl font-bold">
                            {formatPriceWithCurrency(statusData.openBalance)}
                        </div>
                    </div>
                    <div>
                        <div className="text-md text-grey-800">Unpaid CL Invoices</div>
                        <div className="text-xl font-bold">
                            {formatPriceWithCurrency(statusData.unpaidCreditLimitBrutoThisMonth)}
                        </div>
                    </div>
                    <div>
                        <div className="text-md text-grey-800">Open + Unpaid</div>
                        <div className="text-xl font-bold" style={{ color: statusColor }}>
                            {formatPriceWithCurrency(statusData.totalBalance)}
                        </div>
                    </div>
                    <div>
                        <div className="text-md text-grey-800">Current Threshold</div>
                        <div className="text-xl font-bold">
                            {statusData.threshold === null
                                ? '-'
                                : formatPriceWithCurrency(statusData.threshold)}
                        </div>
                    </div>
                    <div>
                        <div className="text-md text-grey-800">Threshold %</div>
                        <div className="text-xl font-bold" style={{ color: statusColor }}>
                            {statusData.percentage === null ? '-' : `${statusData.percentage}%`}
                        </div>
                    </div>
                    <div>
                        <div className="text-md text-grey-800">Block Limit</div>
                        <div className="text-xl font-bold">
                            {statusData.blockLimit === null
                                ? '-'
                                : formatPriceWithCurrency(statusData.blockLimit)}
                        </div>
                    </div>
                </div>
                {statusData.creditLimitReached && (
                    <div className="mt-2 rounded bg-red-50 px-3 py-2 text-md text-red-800">
                        Credit limit reached on{' '}
                        {new Date(statusData.creditLimitReached).toLocaleDateString('en-GB')}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 mt-4">
                <b>Thresholds</b>
                {isDifferentFromBranch && (
                    <span className="rounded bg-tertiary-100 px-2 py-0.5 text-sm text-tertiary-700">
                        Custom (differs from branch)
                    </span>
                )}
                {!isDifferentFromBranch && (
                    <span className="rounded bg-primary-200 px-2 py-0.5 text-sm text-text-secondary-700">
                        Branch defaults
                    </span>
                )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
                {limitsData.effectiveLimits.length === 0 && (
                    <div className="text-grey-800">
                        Credit limits are not enabled for this pro&apos;s main service or the pro
                        has no main service.
                    </div>
                )}
                {limitsData.effectiveLimits.map((limit) => {
                    const isCurrent = limit === statusData.threshold
                    const isBlock = limit === statusData.blockLimit
                    const isCleared = limit <= statusData.creditLimitCleared

                    let bgColor = '#f0f0f0'

                    if (isCurrent) {
                        bgColor = '#fff3e0'
                    } else if (isBlock) {
                        bgColor = '#ffebee'
                    } else if (isCleared) {
                        bgColor = '#e8f5e9'
                    } else if (isCustom) {
                        bgColor = '#e3f2fd'
                    }

                    return (
                        <div
                            key={limit}
                            className="flex items-center rounded px-2 py-1"
                            style={{ background: bgColor }}>
                            <span>
                                {formatPriceWithCurrency(limit)}
                                {isCurrent && (
                                    <span className="ml-1 text-sm text-tertiary-700">
                                        (current)
                                    </span>
                                )}
                                {isBlock && (
                                    <span className="ml-1 text-sm text-red-800">(block)</span>
                                )}
                                {isCleared && !isCurrent && !isBlock && (
                                    <Icon
                                        name="check_circle"
                                        style={{
                                            fontSize: 14,
                                            marginLeft: 4,
                                            color: STATUS_GREEN,
                                        }}
                                    />
                                )}
                            </span>
                            <Restricted location="providers" checkRead={false}>
                                <button
                                    className="ml-1 cursor-pointer border-none bg-transparent p-0.5"
                                    onClick={() => handleRemoveLimit(limit)}
                                    disabled={isSaving}>
                                    <Icon name="close" style={{ fontSize: 14 }} />
                                </button>
                            </Restricted>
                        </div>
                    )
                })}
            </div>
            <Restricted location="providers" checkRead={false}>
                <div className="flex gap-4 items-end mt-6">
                    <div>
                        <div className="text-sm text-grey-700 mb-1">Add threshold</div>
                        <InputField
                            inputClassName="w-36"
                            type="number"
                            value={newLimit}
                            onChange={(e) => setNewLimit(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddLimit()}
                        />
                    </div>
                    <Button
                        className="max-h-[36px]"
                        appearance="primary"
                        onClick={handleAddLimit}
                        disabled={isSaving || !newLimit}>
                        {isSaving ? <LoadingDots /> : 'Add'}
                    </Button>
                </div>
                <div className="flex gap-4 justify-between items-center mt-6">
                    <Button
                        appearance="text_primary"
                        iconBefore="vertical_align_top"
                        onClick={handleSetToNextThreshold}
                        disabled={isSaving || statusData.threshold === null}>
                        {isSaving ? <LoadingDots /> : 'Set to next threshold'}
                    </Button>
                    <Button
                        appearance="text_primary"
                        iconBefore="undo"
                        onClick={handleResetToStandard}
                        disabled={isSaving || !isDifferentFromBranch}>
                        Reset limits to standard
                    </Button>
                </div>
                <div className="flex gap-4 justify-between items-center mt-6">
                    <Button
                        appearance="text_primary"
                        iconBefore="vertical_align_bottom"
                        onClick={handleResetCreditLimitCleared}
                        disabled={isSaving}>
                        {isSaving ? <LoadingDots /> : 'Reset credit limit cleared'}
                    </Button>
                </div>
            </Restricted>
        </div>
    )
}

export default ProviderCreditLimitSettings
